// components/ui/RangeControl.tsx
import React, { memo, useRef, useState, useCallback, useEffect } from 'react';
import CustomRangeSlider from './CustomRangeSlider';

/** Generous upper bound for sourcing-budget sliders; actual brands analysed depend on spend per analysis. */
export const SOURCING_BUDGET_MAX = 30000;

interface RangeControlProps {
  label?: string;
  name?: string;
  min: number;
  max: number;
  step: number;
  value: number | null | undefined;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange?: (value: number) => void;
  onValueCommit?: (value: number) => void;
  setValue?: React.Dispatch<React.SetStateAction<number>>;
  description?: string;
  hideRange?: boolean;
  defaultValue?: number;
  hideLabel?: boolean;
  showValueBox?: boolean;
  allowEmpty?: boolean;
  placeholder?: string;
  // isAnimating prop for CustomRangeSlider (thumb animation) can be kept if still desired
  // For this flash effect, we're using a separate internal state.
}

const formatControlValue = (value: number | null | undefined): string => (
  typeof value === 'number' && Number.isFinite(value) ? String(value) : ''
);

const TEXT_INPUT_STYLE: React.CSSProperties = {
  width: '50px',
  flexShrink: 0,
  borderRadius: '8px',
  color: '#CCCCCC',
  backgroundColor: '#262626',
};

const VALUE_BOX_STYLE: React.CSSProperties = {
  width: '50px',
  flexShrink: 0,
  borderRadius: '8px',
  backgroundColor: '#262626',
  minHeight: 'calc(1.25rem + 2px + 2px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const VALUE_TEXT_STYLE: React.CSSProperties = { color: '#CCCCCC', fontVariantNumeric: 'tabular-nums' };

const RangeControl: React.FC<RangeControlProps> = memo(({
  label,
  name,
  min,
  max,
  step,
  value,
  onChange,
  onValueChange,
  onValueCommit,
  setValue,
  description,
  hideRange = false,
  defaultValue,
  hideLabel = false,
  showValueBox = true,
  allowEmpty = false,
  placeholder = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(formatControlValue(value));
  const textInputRef = useRef<HTMLInputElement>(null);
  
  const [isSpectacleFlashing, setIsSpectacleFlashing] = useState(false);
  const prevValueRef = useRef(value);
  const lastSliderValueRef = useRef<number | null>(null);

  useEffect(() => {
    // Sync inputValue when prop 'value' changes (e.g. from external source or slider)
    // This ensures the displayed text value is up-to-date before any potential flash.
    const formattedValue = formatControlValue(value);
    if (formattedValue !== inputValue && !isEditing) {
        setInputValue(formattedValue);
    }

    // Flash logic
    if (value !== prevValueRef.current && !isEditing) {
      setIsSpectacleFlashing(true);
      const timer = setTimeout(() => {
        setIsSpectacleFlashing(false);
      }, 300); // Flash duration
      
      prevValueRef.current = value;
      return () => clearTimeout(timer);
    }
    
    if (value !== prevValueRef.current) {
        prevValueRef.current = value;
    }
  }, [value, isEditing, inputValue]); // Added inputValue to deps for the first part

  const handleSpanClick = useCallback(() => {
    setIsEditing(true);
    setInputValue(formatControlValue(value)); // Ensure inputValue is current before editing
    setTimeout(() => {
      textInputRef.current?.focus();
      textInputRef.current?.select();
    }, 0);
  }, [value]);

  const handleTextInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    if (/^-?\d*\.?\d*$/.test(rawValue) || rawValue === '' || rawValue === '-') {
        setInputValue(rawValue);
    }
  }, []);

  const emitChange = useCallback((nextValue: number | null) => {
      const eventValue = nextValue === null ? '' : String(nextValue);
      const simulatedEvent = {
          target: { name, value: eventValue, type: 'range' },
          currentTarget: { name, value: eventValue, type: 'range' },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      if (onChange) onChange(simulatedEvent);
      if (nextValue !== null) {
        if (onValueChange) onValueChange(nextValue);
        if (setValue) setValue(nextValue);
      }
  }, [name, onChange, onValueChange, setValue]);

  const processValueChange = useCallback((newValueNum: number | null) => {
      if (allowEmpty && newValueNum === null) {
        setInputValue('');
        if (value !== null && value !== undefined) {
          emitChange(null);
        }
        return;
      }
      if (newValueNum === null) return;
      let finalValue = Math.max(min, Math.min(max, newValueNum));
      if (step > 0) {
        const numSteps = Math.round((finalValue - min) / step);
        finalValue = min + numSteps * step;
        finalValue = Math.max(min, Math.min(max, finalValue));
      }
      setInputValue(String(finalValue)); // Update visual input text

      if (finalValue !== value) {
          emitChange(finalValue);
      }
      onValueCommit?.(finalValue);
  }, [allowEmpty, min, max, step, emitChange, value, onValueCommit]);


  const handleInputBlur = useCallback(() => {
    setIsEditing(false);
    const trimmedInput = inputValue.trim();
    if (allowEmpty && trimmedInput.length === 0) {
      processValueChange(null);
      return;
    }
    let parsedValue = parseFloat(trimmedInput);
    if (isNaN(parsedValue)) {
      parsedValue = typeof value === 'number' && Number.isFinite(value) ? value : defaultValue ?? min;
    }
    processValueChange(parsedValue);
  }, [allowEmpty, inputValue, value, defaultValue, min, processValueChange]);

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      textInputRef.current?.blur();
    } else if (e.key === 'Escape') {
      setInputValue(formatControlValue(value));
      setIsEditing(false);
    }
  }, [value]);

  const handleInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);

  const handleSliderChange = useCallback((newValue: number) => {
    setInputValue(String(newValue));
    lastSliderValueRef.current = newValue;
     if (newValue !== value) {
        emitChange(newValue);
     }
  }, [emitChange, value]);

  const handleSliderInteractionEnd = useCallback(() => {
    const committedValue = lastSliderValueRef.current;
    lastSliderValueRef.current = null;
    if (committedValue !== null) {
      onValueCommit?.(committedValue);
    }
  }, [onValueCommit]);


  const containerClasses = showValueBox ? 'flex items-center space-x-4' : 'flex items-center';

  return (
    <>
      {!hideLabel && label && (
        <label className="block text-sm font-medium text-white mb-1">{label}</label>
      )}
      <div className={containerClasses}>
        {showValueBox && (
          isEditing ? (
            <input
              ref={textInputRef}
              type="text"
              inputMode="numeric"
              className="bg-black rounded-md px-2 py-1 text-center text-sm focus:outline-none focus:ring-1 focus:ring-[#B3D9FF]"
              style={TEXT_INPUT_STYLE}
              value={inputValue}
              onChange={handleTextInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              onFocus={handleInputFocus}
              autoFocus
            />
          ) : (
            <div
               className={`bg-black rounded-md px-2 py-1 text-center cursor-pointer ${isSpectacleFlashing ? 'spectacle-flash' : ''}`}
              style={VALUE_BOX_STYLE}
              onClick={handleSpanClick}
            >
              <span className="text-sm font-mono" style={VALUE_TEXT_STYLE}>
                  {typeof value === 'number' && Number.isFinite(value)
                    ? parseFloat(value.toFixed(step.toString().split('.')[1]?.length || 0))
                    : placeholder}
              </span>
            </div>
          )
        )}
        {!hideRange && (
          <div className="w-full">
              <CustomRangeSlider
                min={min}
                max={max}
                step={step}
                value={typeof value === 'number' && Number.isFinite(value) ? value : min}
                onChange={handleSliderChange}
                onInteractionEnd={handleSliderInteractionEnd}
              />
          </div>
        )}
      </div>
      {description && <p className="text-xs text-[#666666] mt-1">{description}</p>}
    </>
  );
});

RangeControl.displayName = "RangeControl";
export default RangeControl;
